import React from "react";
import { ROUTS } from "./routs";
import { Trans } from "@lingui/macro";
import { useSelector } from "react-redux";
import { edmIsEnabled } from "../utils/edmIsEnabled";
import { Loader } from "../components/UI/Loader/Loader";
import { Redirect, Route, Switch } from "react-router-dom";
import { selectUserPosition } from "../model/system/selectors";
import { NotFoundPage } from "../components/pages/NotFoundPage";
import { POSITION_WITH_FULL_ACCESS } from "../constants/userPositions";
import { DataSales } from "../components/containers/dashboard/dataSales";
import { DataUsers } from "../components/containers/dashboard/dataUsers";
import { DataOrders } from "../components/containers/dashboard/dataOrders";
import { DataEncash } from "../components/containers/dashboard/dataEncash";
import { DataStores } from "../components/containers/dashboard/dataStores";
import { DataTickets } from "../components/containers/dashboard/dataTickets";
import { DataRefunds } from "../components/containers/dashboard/dataRefunds";
import { DataSettings } from "../components/containers/dashboard/dataSettings";
import { DataZreports } from "../components/containers/dashboard/dataZreports";
import { DataCashDesks } from "../components/containers/dashboard/dataCashDesk";
import { DataProductInfo } from "../components/containers/dashboard/dataProducts";
import { DataCategories } from "../components/containers/dashboard/dataCategories";
import { DataStatistics } from "../components/containers/dashboard/dataStatistics";
import { DataSalesArchive } from "../components/containers/dashboard/dataSalesArchive";
import { EdmDataProductInfo } from "../components/containers/dashboard/dataEdmProducts";
import { DataOrdersArchive } from "../components/containers/dashboard/dataOrdersArchive";
import { DataRefundsArchive } from "../components/containers/dashboard/dataRefundsArchive";
import { DataHandoverRecords } from "../components/containers/dashboard/dataHandoverRecord";
import { DataPosConfiguration } from "../components/containers/dashboard/dataPosConfiguration";
import { DataTransferCashDesks } from "../components/containers/dashboard/dataTransferCashDesks";
import {
  STATISTIC_TYPES,
  StatisticsDetailPage,
} from "../components/containers/dashboard/dataStatistics/detailsPage";

export type typeDashboardRouter = {
  setSidebarOpen: React.Dispatch<React.SetStateAction<boolean>>;
};

export const DashboardRouter: React.FC<typeDashboardRouter> = ({
  setSidebarOpen,
}) => {
  const position = useSelector(selectUserPosition);

  if (!position) {
    return <Loader />;
  }

  return POSITION_WITH_FULL_ACCESS.includes(position) ? (
    <Switch>
      <Route
        path={ROUTS.HOME_DASHBOARD}
        exact
        render={() => <Redirect to={ROUTS.COMMODITY_PRODUCTS_DASHBOARD} />}
      />

      {process.env.REACT_APP_CUSTOMER !== "SKAPPSK" && (
        <Route
          path={ROUTS.SUMUP_CLOSINGZREPORT_DASHBOARD}
          exact
          render={() => <DataZreports setSidebarOpen={setSidebarOpen} />}
        />
      )}

      <Route
        path={ROUTS.SUMUP_SALES_DASHBOARD}
        exact
        render={() => <DataSales setSidebarOpen={setSidebarOpen} />}
      />

      <Route
        path={ROUTS.SUMUP_REFUNDS_DASHBOARD}
        exact
        render={() => <DataRefunds setSidebarOpen={setSidebarOpen} />}
      />

      {process.env.REACT_APP_ENCASHMENT === "true" && (
        <Route
          path={ROUTS.SUMUP_ENCASHMENT_DASHBOARD}
          exact
          render={() => <DataEncash setSidebarOpen={setSidebarOpen} />}
        />
      )}

      <Route
        path={ROUTS.SUMUP_PAYMENTSUMMARY_DASHBOARD}
        exact
        component={NotFoundPage}
      />

      <Route
        path={ROUTS.SUMUP_HANDOVERRECORD_DASHBOARD}
        exact
        render={() => <DataHandoverRecords setSidebarOpen={setSidebarOpen} />}
      />

      <Route
        path={ROUTS.COMMODITY_PRODUCTS_DASHBOARD}
        exact
        render={() => <DataProductInfo setSidebarOpen={setSidebarOpen} />}
      />

      <Route
        path={ROUTS.COMMODITY_COMMODITYPACKAGE_DASHBOARD}
        exact
        component={NotFoundPage}
      />

      <Route
        path={ROUTS.COMMODITY_CATEGORIES_DASHBOARD}
        exact
        render={() => <DataCategories setSidebarOpen={setSidebarOpen} />}
      />

      {edmIsEnabled && (
        <Route
          exact
          path={ROUTS.WAREHOUSE_PRODUCTS_DASHBOARD}
          render={() => <EdmDataProductInfo setSidebarOpen={setSidebarOpen} />}
        />
      )}

      <Route
        path={ROUTS.STORE_STORES_DASHBOARD}
        exact
        render={() => <DataStores setSidebarOpen={setSidebarOpen} />}
      />

      <Route
        path={ROUTS.STORE_CASHIERDESKMANAGEMENT_DASHBOARD}
        exact
        render={() => <DataCashDesks setSidebarOpen={setSidebarOpen} />}
      />

      <Route
        path={ROUTS.STAFF_DASHBOARD}
        exact
        render={() => <DataUsers setSidebarOpen={setSidebarOpen} />}
      />

      <Route path={ROUTS.FILEEXPORT_DASHBOARD} exact component={NotFoundPage} />

      <Route
        path={ROUTS.SUPPORT_DASHBOARD}
        exact
        render={() => <DataTickets setSidebarOpen={setSidebarOpen} />}
      />

      <Route
        path={ROUTS.ORDERS_DASHBOARD}
        exact
        render={() => <DataOrders setSidebarOpen={setSidebarOpen} />}
      />

      <Route
        path={ROUTS.TRANSFER_CASHDESK_DASHBOARD}
        exact
        render={() => <DataTransferCashDesks setSidebarOpen={setSidebarOpen} />}
      />

      <Route
        path={ROUTS.SETTINGS_POS_CONFIGURATION_DASHBOARD}
        exact
        render={() => <DataPosConfiguration setSidebarOpen={setSidebarOpen} />}
      />

      <Route
        path={ROUTS.SETTINGS_DASHBOARD}
        exact
        render={() => <DataSettings setSidebarOpen={setSidebarOpen} />}
      />

      <Route
        path={ROUTS.STATISTICS_DASHBOARD}
        exact
        render={() => <DataStatistics setSidebarOpen={setSidebarOpen} />}
      />

      <Route
        path={ROUTS.STATISTICS_SALES_AMOUNT_DASHBOARD}
        exact
        render={() => (
          <StatisticsDetailPage
            title={<Trans>Sales amount</Trans>}
            statisticType={STATISTIC_TYPES.SALES_AMOUNT}
            setSidebarOpen={setSidebarOpen}
          />
        )}
      />

      <Route
        path={ROUTS.STATISTICS_CHECKS_COUNT_DASHBOARD}
        exact
        render={() => (
          <StatisticsDetailPage
            title={<Trans>Count of checks</Trans>}
            statisticType={STATISTIC_TYPES.CHECKS_COUNT}
            setSidebarOpen={setSidebarOpen}
          />
        )}
      />

      <Route
        path={ROUTS.STATISTICS_AVERAGE_CHECK_DASHBOARD}
        exact
        render={() => (
          <StatisticsDetailPage
            title={<Trans>Average check</Trans>}
            statisticType={STATISTIC_TYPES.AVERAGE_CHECK}
            setSidebarOpen={setSidebarOpen}
          />
        )}
      />

      <Route
        path={ROUTS.STATISTICS_REFUNDS_AMOUNT_DASHBOARD}
        exact
        render={() => (
          <StatisticsDetailPage
            title={<Trans>Refunds amount</Trans>}
            statisticType={STATISTIC_TYPES.REFUNDS_AMOUNT}
            setSidebarOpen={setSidebarOpen}
          />
        )}
      />

      <Route
        path={ROUTS.STATISTICS_REFUNDS_COUNT_DASHBOARD}
        exact
        render={() => (
          <StatisticsDetailPage
            title={<Trans>Count of refunds</Trans>}
            statisticType={STATISTIC_TYPES.REFUNDS_COUNT}
            setSidebarOpen={setSidebarOpen}
          />
        )}
      />

      <Route
        path={ROUTS.STATISTICS_PAYMENT_METHODS_DASHBOARD}
        exact
        render={() => (
          <StatisticsDetailPage
            title={<Trans>Payment methods</Trans>}
            statisticType={STATISTIC_TYPES.PAYMENT_METHODS}
            setSidebarOpen={setSidebarOpen}
          />
        )}
      />

      <Route
        path={ROUTS.STATISTICS_PAYMENT_TYPES_DASHBOARD}
        exact
        render={() => (
          <StatisticsDetailPage
            title={<Trans>Payment types</Trans>}
            statisticType={STATISTIC_TYPES.PAYMENT_TYPES}
            setSidebarOpen={setSidebarOpen}
          />
        )}
      />

      <Route
        path={ROUTS.STATISTICS_DAILY_DASHBOARD}
        exact
        render={() => (
          <StatisticsDetailPage
            title={<Trans>Daily statistic</Trans>}
            statisticType={STATISTIC_TYPES.DAILY_STATISTIC}
            setSidebarOpen={setSidebarOpen}
          />
        )}
      />

      <Route
        path={ROUTS.STATISTICS_EMPLOYEE_DASHBOARD}
        exact
        render={() => (
          <StatisticsDetailPage
            title={<Trans>Employees statistic</Trans>}
            statisticType={STATISTIC_TYPES.EMPLOYEE_STATISTIC}
            setSidebarOpen={setSidebarOpen}
          />
        )}
      />

      <Route
        path={ROUTS.STATISTICS_PRODUCT_DASHBOARD}
        exact
        render={() => (
          <StatisticsDetailPage
            title={<Trans>Products statistic</Trans>}
            statisticType={STATISTIC_TYPES.PRODUCT_STATISTIC}
            setSidebarOpen={setSidebarOpen}
          />
        )}
      />

      <Route
        exact
        path={ROUTS.ARCHIVE}
        render={() => <Redirect to={ROUTS.ARCHIVE_SALES_DASHBOARD} />}
      />

      <Route
        exact
        path={ROUTS.ARCHIVE_SALES_DASHBOARD}
        render={() => <DataSalesArchive setSidebarOpen={setSidebarOpen} />}
      />

      <Route
        exact
        path={ROUTS.ARCHIVE_REFUNDS_DASHBOARD}
        render={() => <DataRefundsArchive setSidebarOpen={setSidebarOpen} />}
      />

      <Route
        exact
        path={ROUTS.ARCHIVE_ORDERS_DASHBOARD}
        render={() => <DataOrdersArchive setSidebarOpen={setSidebarOpen} />}
      />

      <Route path={ROUTS.HOME_PAGE} component={NotFoundPage} />
    </Switch>
  ) : (
    <Switch>
      <Route
        path={ROUTS.HOME_DASHBOARD}
        exact
        render={() => <Redirect to={ROUTS.SUMUP_SALES_DASHBOARD} />}
      />

      {process.env.REACT_APP_CUSTOMER !== "SKAPPSK" && (
        <Route
          path={ROUTS.SUMUP_CLOSINGZREPORT_DASHBOARD}
          exact
          render={() => <DataZreports setSidebarOpen={setSidebarOpen} />}
        />
      )}

      <Route
        path={ROUTS.SUMUP_SALES_DASHBOARD}
        exact
        render={() => <DataSales setSidebarOpen={setSidebarOpen} />}
      />
      <Route
        path={ROUTS.SUMUP_REFUNDS_DASHBOARD}
        exact
        render={() => <DataRefunds setSidebarOpen={setSidebarOpen} />}
      />
      <Route
        path={ROUTS.SUMUP_PAYMENTSUMMARY_DASHBOARD}
        exact
        component={NotFoundPage}
      />
      <Route
        path={ROUTS.SUMUP_HANDOVERRECORD_DASHBOARD}
        exact
        render={() => <DataHandoverRecords setSidebarOpen={setSidebarOpen} />}
      />

      <Route
        path={ROUTS.COMMODITY_PRODUCTS_DASHBOARD}
        exact
        render={() => <DataProductInfo setSidebarOpen={setSidebarOpen} />}
      />
      <Route
        path={ROUTS.COMMODITY_COMMODITYPACKAGE_DASHBOARD}
        exact
        component={NotFoundPage}
      />
      <Route
        path={ROUTS.COMMODITY_CATEGORIES_DASHBOARD}
        exact
        render={() => <DataCategories setSidebarOpen={setSidebarOpen} />}
      />

      {edmIsEnabled && (
        <Route
          exact
          path={ROUTS.WAREHOUSE_PRODUCTS_DASHBOARD}
          render={() => <EdmDataProductInfo setSidebarOpen={setSidebarOpen} />}
        />
      )}

      <Route
        path={ROUTS.STORE_CASHIERDESKMANAGEMENT_DASHBOARD}
        exact
        render={() => <DataCashDesks setSidebarOpen={setSidebarOpen} />}
      />

      <Route path={ROUTS.FILEEXPORT_DASHBOARD} exact component={NotFoundPage} />

      <Route
        path={ROUTS.ORDERS_DASHBOARD}
        exact
        render={() => <DataOrders setSidebarOpen={setSidebarOpen} />}
      />

      <Route
        path={ROUTS.TRANSFER_CASHDESK_DASHBOARD}
        exact
        render={() => <DataTransferCashDesks setSidebarOpen={setSidebarOpen} />}
      />

      <Route
        path={ROUTS.SETTINGS_DASHBOARD}
        exact
        render={() => <DataSettings setSidebarOpen={setSidebarOpen} />}
      />

      <Route
        exact
        path={ROUTS.ARCHIVE}
        render={() => <Redirect to={ROUTS.ARCHIVE_SALES_DASHBOARD} />}
      />
      <Route
        exact
        path={ROUTS.ARCHIVE_SALES_DASHBOARD}
        render={() => <DataSalesArchive setSidebarOpen={setSidebarOpen} />}
      />
      <Route
        exact
        path={ROUTS.ARCHIVE_REFUNDS_DASHBOARD}
        render={() => <DataRefundsArchive setSidebarOpen={setSidebarOpen} />}
      />
      <Route
        exact
        path={ROUTS.ARCHIVE_ORDERS_DASHBOARD}
        render={() => <DataOrdersArchive setSidebarOpen={setSidebarOpen} />}
      />

      <Route path={ROUTS.HOME_PAGE} component={NotFoundPage} />
    </Switch>
  );
};
